 import { 
   Warehouse, 
   Package, 
   Truck, 
   Snowflake, 
   Factory, 
   ShoppingCart, 
   Building2, 
   Layers,
   ArrowLeftRight,
   Timer,
   type LucideIcon 
 } from "lucide-react";
 
 export interface RackSpec {
   label: string;
   value: string;
 }
 
 export interface UseCase {
   industry: string;
   icon: LucideIcon;
   description: string;
 }
 
 export interface FAQ {
   question: string;
   answer: string;
 }
 
 export interface Compliance {
   rmi: boolean;
   ansi: boolean;
   osha: boolean;
   seismic?: boolean;
 }
 
export interface ConstructionVariant {
    id: string;
    name: string;
    anchor: string;
    tagline: string;
    description: string;
    specs: RackSpec[];
    features: string[];
    pros: string[];
    cons: string[];
    faqs: FAQ[];
  }

  export interface RackType {
    id: string;
    name: string;
    slug: string;
    tagline: string;
    shortDescription: string;
    description: string;
    heroImage?: string;
    specs: RackSpec[];
    features: string[];
    pros: string[];
    cons: string[];
    useCases: UseCase[];
    compliance: Compliance;
    faqs: FAQ[];
    relatedProducts: string[];
    keyBenefit: string;
    densityRating: number; // 1-5 scale
    accessibilityRating: number; // 1-5 scale
    costEfficiency: string;
    constructionVariants?: ConstructionVariant[];
    /** If true, this entry is merged into another page and should not appear in navigation lists */
    mergedInto?: string;
  }
 
 export const rackTypes: Record<string, RackType> = {
   teardrop: {
     id: "teardrop",
     name: "Teardrop Racking",
     slug: "teardrop",
     tagline: "The Industry Standard for Universal Compatibility",
     shortDescription: "Tool-free assembly with the most versatile beam connections in warehouse storage.",
     description: "Teardrop racking is the most widely used pallet racking system in North America. Named for its distinctive teardrop-shaped connector holes, this system allows for quick, tool-free beam adjustment and universal compatibility across manufacturers. Whether you're outfitting a new warehouse or expanding existing operations, teardrop racking offers unmatched flexibility and ease of reconfiguration.",
     specs: [
       { label: "Beam Connection", value: "3\" Teardrop Pattern" },
       { label: "Upright Heights", value: "8' to 48'" },
       { label: "Beam Lengths", value: "48\" to 144\"" },
       { label: "Weight Capacity", value: "1,000 to 30,000 lbs per level (configuration-specific†)" },
       { label: "Upright Depth", value: "36\" to 48\"" },
       { label: "Steel Gauge", value: "14 to 11 gauge" },
       { label: "Assembly", value: "Tool-free beam installation" },
       { label: "Compatibility", value: "Universal manufacturer cross-compatibility" },
     ],
     features: [
       "Tool-free beam installation and adjustment",
       "Universal manufacturer compatibility",
       "Easy height reconfiguration",
       "Wide range of accessories available",
       "Proven reliability for decades",
       "Most commonly available in used market",
     ],
     pros: [
       "Fastest installation time of any rack system",
       "Easy to reconfigure as needs change",
       "Compatible with most warehouse accessories",
       "Largest selection of new and used inventory",
       "Cost-effective for most applications",
       "Simple maintenance and repairs",
     ],
     cons: [
       "Lower density than drive-in systems",
       "Requires aisle space between rows",
       "May not suit specialized storage needs",
     ],
     useCases: [
       { industry: "General Warehousing", icon: Warehouse, description: "Standard pallet storage for diverse inventory types" },
       { industry: "E-Commerce Fulfillment", icon: ShoppingCart, description: "Fast-moving SKUs requiring quick access" },
       { industry: "Distribution Centers", icon: Truck, description: "High-throughput operations with varied product mix" },
       { industry: "Manufacturing", icon: Factory, description: "Raw materials and finished goods storage" },
       { industry: "Third-Party Logistics", icon: Package, description: "Flexible storage for multiple clients" },
     ],
     compliance: {
       rmi: true,
       ansi: true,
       osha: true,
       seismic: true,
     },
     faqs: [
       {
         question: "What makes teardrop racking 'universal'?",
         answer: "Teardrop racking uses a standardized 3-inch step pattern with teardrop-shaped holes. This design has become the industry standard, meaning beams from most major manufacturers are interchangeable, giving you flexibility in sourcing and expanding your system.",
       },
       {
          question: "How much weight can teardrop racking hold?",
          answer: "Load capacity is configuration-specific — it depends on beam length, upright gauge, frame height, and seismic zone. Typical ranges are 1,000 to 30,000 lbs per beam level, but actual ratings must be determined by a qualified engineer. Every installation should include a LARC (Load Application and Rack Configuration) plaque displaying the rated capacity. We provide engineering and LARC documentation with every system.",
        },
       {
          question: "Is used teardrop racking safe?",
          answer: "Yes, when properly inspected and rated. We thoroughly evaluate all used racking for structural integrity, checking for damage, rust, and wear. Every system we sell is engineered to meet ANSI/MH16.1 and RMI guidelines. We provide condition reports, load ratings, and LARC documentation. We recommend scheduling regular inspections and installing column protectors to maintain safety over time.",
        },
       {
         question: "How quickly can teardrop racking be installed?",
         answer: "Teardrop's tool-free assembly makes it the fastest racking system to install. A typical warehouse setup takes 2-5 days depending on size. Our installation teams are experienced and efficient, minimizing disruption to your operations.",
       },
       {
         question: "Can I mix teardrop beams from different manufacturers?",
         answer: "In most cases, yes. The 3-inch teardrop pattern is standardized across major manufacturers including Interlake Mecalux, Ridg-U-Rak, Frazier, and others. However, we always verify compatibility before combining systems.",
       },
     ],
     relatedProducts: ["selective", "push-back"],
     keyBenefit: "Universal Compatibility",
     densityRating: 3,
     accessibilityRating: 5,
      costEfficiency: "Best value for general warehousing",
      mergedInto: "selective",
    },
 
   selective: {
     id: "selective",
     name: "Selective Racking",
     slug: "selective",
     tagline: "100% Pallet Accessibility for Maximum Inventory Control",
     shortDescription: "Direct access to every pallet position for FIFO inventory management.",
      description: "Selective racking provides immediate access to every pallet position in your warehouse. This system is ideal for operations requiring FIFO (First-In, First-Out) inventory rotation, high SKU counts, or frequent picking. Available in three construction styles — Roll-Formed (Teardrop), Structural (Bolted), and Tubular — each suited to different load, environment, and budget requirements.",
      specs: [
        { label: "Access Type", value: "100% Direct Pallet Access" },
        { label: "Inventory Flow", value: "FIFO (First-In, First-Out)" },
        { label: "Upright Heights", value: "8' to 40'" },
        { label: "Beam Lengths", value: "48\" to 120\"" },
         { label: "Weight Capacity", value: "2,000 to 25,000 lbs per level (configuration-specific†)" },
        { label: "Aisle Width", value: "8' to 12' typical" },
        { label: "Construction Styles", value: "Roll-Formed (Teardrop), Structural (Bolted), Tubular" },
        { label: "Configuration", value: "Single or double-deep rows" },
      ],
      features: [
        "Direct access to every pallet location",
        "FIFO inventory rotation support",
        "Compatible with all forklift types",
        "Flexible layout configurations",
        "Easy to expand or reconfigure",
        "Supports pick tunnels and cross-aisles",
        "Available in Roll-Formed, Structural, and Tubular construction",
      ],
      pros: [
        "100% selectivity – access any pallet instantly",
        "Perfect for high-SKU environments",
        "Enables strict FIFO rotation",
        "Low product damage risk",
        "Easy visual inventory checks",
        "Most versatile rack configuration",
      ],
      cons: [
        "Lower storage density than drive-in systems",
        "Requires more aisle space",
        "Higher cost per pallet position than bulk storage",
      ],
      useCases: [
        { industry: "Food Distribution", icon: Package, description: "Date-sensitive products requiring FIFO rotation" },
        { industry: "Retail Distribution", icon: ShoppingCart, description: "High SKU counts with varying demand" },
        { industry: "Cold Storage", icon: Snowflake, description: "Temperature-controlled environments" },
        { industry: "Wholesale Operations", icon: Building2, description: "Mixed case and pallet picking" },
        { industry: "Manufacturing", icon: Factory, description: "Component storage with frequent access" },
      ],
      compliance: {
        rmi: true,
        ansi: true,
        osha: true,
        seismic: true,
      },
      faqs: [
        {
          question: "What's the difference between selective and teardrop racking?",
          answer: "Teardrop refers to the beam-to-upright connection style (roll-formed construction), while selective describes the storage configuration. Most selective racking uses teardrop connections. Selective means every pallet is directly accessible from an aisle.",
        },
        {
          question: "What construction styles are available for selective racking?",
          answer: "Selective racking comes in three construction styles: Roll-Formed (Teardrop) is the industry standard with tool-free assembly and universal compatibility; Structural uses hot-rolled steel channels with bolted connections for maximum impact resistance; and Tubular uses closed-section tubes for hygienic, food-grade environments.",
        },
        {
          question: "How much aisle space does selective racking require?",
          answer: "Standard selective layouts need 8-12 foot aisles for conventional forklifts. Narrow-aisle configurations with specialized equipment can reduce this to 5-7 feet, increasing storage density while maintaining full selectivity.",
        },
        {
          question: "Is selective racking good for cold storage?",
          answer: "Yes, selective racking is excellent for cold storage. The open design allows proper air circulation for temperature control, and full accessibility makes inventory rotation easier – critical for temperature-sensitive products.",
        },
        {
          question: "Can selective racking support pick tunnels?",
          answer: "Absolutely. Selective racking easily accommodates pick tunnels, cross-aisles, and mezzanine integrations. This flexibility makes it ideal for operations combining bulk storage with case-pick or piece-pick areas.",
        },
      ],
      constructionVariants: [
        {
          id: "roll-formed",
          name: "Roll-Formed (Teardrop)",
          anchor: "roll-formed",
          tagline: "The Industry Standard for Universal Compatibility",
          description: "Teardrop racking is the most widely used pallet racking system in North America. Named for its distinctive teardrop-shaped connector holes, this system allows for quick, tool-free beam adjustment and universal compatibility across manufacturers.",
          specs: [
            { label: "Beam Connection", value: "3\" Teardrop Pattern" },
            { label: "Steel Gauge", value: "14 to 11 gauge" },
            { label: "Assembly", value: "Tool-free beam installation" },
            { label: "Compatibility", value: "Universal manufacturer cross-compatibility" },
            { label: "Cost Tier", value: "Low-Medium (most economical)" },
          ],
          features: [
            "Tool-free beam installation and adjustment",
            "Universal manufacturer compatibility",
            "Easy height reconfiguration",
            "Largest selection of new and used inventory",
            "Most commonly available in used market",
          ],
          pros: [
            "Fastest installation time of any rack system",
            "Easy to reconfigure as needs change",
            "Largest selection of new and used inventory",
            "Cost-effective for most applications",
          ],
          cons: [
            "Lower impact resistance than structural",
            "May not suit extreme load or outdoor environments",
          ],
          faqs: [
            { question: "What makes teardrop racking 'universal'?", answer: "Teardrop racking uses a standardized 3-inch step pattern with teardrop-shaped holes. This design has become the industry standard, meaning beams from most major manufacturers are interchangeable." },
            { question: "Can I mix teardrop beams from different manufacturers?", answer: "In most cases, yes. The 3-inch teardrop pattern is standardized across major manufacturers including Interlake Mecalux, Ridg-U-Rak, Frazier, and others. However, we always verify compatibility before combining systems." },
          ],
        },
        {
          id: "structural",
          name: "Structural (Bolted)",
          anchor: "structural",
          tagline: "Heavy-Duty Steel for Demanding Environments",
          description: "Structural pallet racking uses hot-rolled steel channels and wide-flange beams with bolted connections. This produces significantly higher impact resistance, making it ideal for high-traffic warehouses, outdoor installations, and cold storage.",
          specs: [
            { label: "Steel Type", value: "Hot-rolled C-channels & I-beams" },
            { label: "Connection", value: "Bolted beam-to-column" },
            { label: "Weight Capacity", value: "3,000 to 40,000 lbs per level" },
            { label: "Upright Gauge", value: "C3 to C8 structural channels" },
            { label: "Cost Tier", value: "Medium-High (20-40% more than roll-formed)" },
          ],
          features: [
            "Hot-rolled steel construction for superior strength",
            "Bolted connections resist forklift impacts",
            "Individual damaged components easily replaced",
            "Outdoor and cold storage rated",
          ],
          pros: [
            "Superior impact resistance vs roll-formed",
            "Individual beam/column replacement without disassembly",
            "Ideal for high-traffic forklift environments",
            "Longer service life reduces total cost of ownership",
          ],
          cons: [
            "Higher upfront cost than roll-formed",
            "Heavier components require more labor to install",
            "Not universally cross-compatible between manufacturers",
          ],
          faqs: [
            { question: "When should I choose structural over teardrop?", answer: "Choose structural for high-traffic areas, outdoor/cold storage, very heavy loads (over 10,000 lbs per level), or when forklift damage is a recurring problem." },
            { question: "Can structural racking be used in drive-in configurations?", answer: "Yes, structural steel is actually preferred for drive-in racking because the forklift enters the rack structure. The bolted connections and heavier steel resist the inevitable impacts better." },
          ],
        },
        {
          id: "tubular",
          name: "Tubular",
          anchor: "tubular",
          tagline: "Round-Tube Construction for Clean & Hygienic Storage",
          description: "Tubular pallet racking uses round or square hollow steel tubes instead of open C-channel uprights. The closed-section design prevents dust, debris, and moisture from accumulating, making it ideal for food processing, pharmaceutical, and cleanroom environments.",
          specs: [
            { label: "Steel Type", value: "Closed-section round or square tubes" },
            { label: "Weight Capacity", value: "2,000 to 15,000 lbs per level" },
            { label: "Surface Finish", value: "Powder-coated or galvanized" },
            { label: "Connection", value: "Proprietary bolt or clip systems" },
            { label: "Cost Tier", value: "Medium-High" },
          ],
          features: [
            "Closed uprights prevent dust and debris accumulation",
            "Easy to clean and sanitize",
            "Galvanized options for washdown environments",
            "Resistant to corrosion in humid environments",
          ],
          pros: [
            "Superior hygiene – no hidden debris traps",
            "Meets food-grade and pharmaceutical storage standards",
            "Better corrosion resistance than open-channel steel",
            "Clean, professional appearance",
          ],
          cons: [
            "Higher cost than standard roll-formed racking",
            "Proprietary connections limit cross-compatibility",
            "Limited availability in used market",
          ],
          faqs: [
            { question: "Why choose tubular over standard pallet racking?", answer: "Tubular racking's closed-section uprights prevent dust, moisture, and debris from collecting inside the frame. Essential for food, pharmaceutical, and cleanroom applications." },
            { question: "Is tubular racking FDA compliant?", answer: "Tubular racking supports FDA compliance for food storage when combined with proper finishes (galvanized or food-grade powder coating) and appropriate spacing." },
          ],
        },
      ],
      relatedProducts: ["push-back", "vna", "double-deep"],
     keyBenefit: "100% Pallet Accessibility",
     densityRating: 2,
     accessibilityRating: 5,
     costEfficiency: "Optimal for high-turn inventory",
   },
 
   "drive-in": {
     id: "drive-in",
     name: "Drive-In Racking",
     slug: "drive-in",
     tagline: "Maximum Density for Bulk Storage Operations",
     shortDescription: "Up to 75% more storage capacity with forklift-accessible deep lanes.",
     description: "Drive-in racking maximizes warehouse density by allowing forklifts to drive directly into the rack structure. Pallets are stored on continuous rails, creating deep lanes that can hold 6-10+ pallets per lane. This system is ideal for bulk storage of homogeneous products with low SKU counts and LIFO (Last-In, First-Out) inventory flow.",
     specs: [
       { label: "Storage Depth", value: "6 to 12+ pallets deep" },
       { label: "Inventory Flow", value: "LIFO (Last-In, First-Out)" },
       { label: "Upright Heights", value: "12' to 45'" },
       { label: "Lane Width", value: "8' to 12' typical" },
        { label: "Weight Capacity", value: "2,500 to 3,000 lbs per pallet (configuration-specific†)" },
       { label: "Density Increase", value: "Up to 75% vs. selective" },
       { label: "Entry Points", value: "Single-entry or drive-through" },
       { label: "Rail Type", value: "Structural or roll-formed" },
     ],
     features: [
       "Forklift drives directly into rack structure",
       "Deep lane storage (6-12+ pallets)",
       "Eliminates multiple access aisles",
       "Structural steel construction",
       "Ideal for homogeneous product storage",
       "Compatible with cold storage environments",
     ],
     pros: [
       "Up to 75% more storage than selective",
       "Lowest cost per pallet position",
       "Perfect for seasonal bulk storage",
       "Excellent for cold storage applications",
       "Maximizes cubic utilization",
       "Reduces total warehouse footprint needed",
     ],
     cons: [
       "LIFO access limits product rotation",
       "Lower selectivity – entire lane same SKU",
       "Higher potential for rack damage",
       "Slower put-away and retrieval",
       "Requires experienced forklift operators",
     ],
     useCases: [
       { industry: "Cold Storage", icon: Snowflake, description: "Frozen food and beverage bulk storage" },
       { industry: "Food Manufacturing", icon: Factory, description: "Raw ingredients and finished goods" },
       { industry: "Seasonal Storage", icon: Timer, description: "Holiday merchandise and seasonal products" },
       { industry: "Beverage Distribution", icon: Package, description: "High-volume single-SKU storage" },
       { industry: "Paper Products", icon: Layers, description: "Bulk tissue, paper, and packaging materials" },
     ],
     compliance: {
       rmi: true,
       ansi: true,
       osha: true,
       seismic: true,
     },
     faqs: [
       {
         question: "How much space can drive-in racking save?",
         answer: "Drive-in systems typically provide 50-75% more storage capacity than selective racking in the same footprint. By eliminating access aisles between rows, you maximize usable storage space. This is especially valuable in cold storage where cubic footage is expensive.",
       },
       {
         question: "What's the difference between drive-in and drive-through?",
         answer: "Drive-in has one entry point (LIFO access), while drive-through has entries on both ends (FIFO possible). Drive-through requires more floor space but enables first-in, first-out rotation for date-sensitive products.",
       },
       {
          question: "Is drive-in racking safe?",
          answer: "Yes, when properly designed, installed, and maintained per ANSI/MH16.1. Drive-in systems require careful structural engineering, trained forklift operators, and regular inspections. We include guide rails, column protectors, and impact protection in all installations. A LARC plaque documenting the rated capacity must be posted, and we recommend scheduling annual professional inspections.",
        },
       {
         question: "What products work best in drive-in racking?",
         answer: "Drive-in excels with homogeneous, high-volume products: cases of beverages, frozen foods, seasonal items, and bulk raw materials. Products should be stable on pallets and have consistent dimensions. Low SKU count is ideal.",
       },
       {
         question: "Can drive-in racking handle seismic requirements?",
         answer: "Yes. We engineer drive-in systems to meet local seismic codes, including California requirements. Structural steel construction, anchor bolt specifications, and bracing are calculated for your specific location and load requirements.",
       },
     ],
     relatedProducts: ["push-back", "selective"],
     keyBenefit: "Maximum Storage Density",
     densityRating: 5,
     accessibilityRating: 1,
     costEfficiency: "Lowest cost per pallet position",
   },
 
   "push-back": {
     id: "push-back",
     name: "Push-Back Racking",
     slug: "push-back",
     tagline: "Dynamic Storage Without Forklift Lane Entry",
     shortDescription: "Gravity-fed retrieval system storing 2-6 pallets deep per lane.",
     description: "Push-back racking combines high density with improved selectivity. Pallets are stored on nested carts that roll on inclined rails. When a pallet is removed from the front, the remaining pallets automatically roll forward. This gravity-fed system provides LIFO access while keeping forklifts safely in the aisle.",
     specs: [
       { label: "Storage Depth", value: "2 to 6 pallets deep" },
       { label: "Inventory Flow", value: "LIFO (Last-In, First-Out)" },
       { label: "Upright Heights", value: "12' to 40'" },
       { label: "Cart Capacity", value: "2,500 to 3,000 lbs per pallet (configuration-specific†)" },
       { label: "Rail Pitch", value: "1.5\" to 2\" per foot" },
       { label: "Density Increase", value: "Up to 50% vs. selective" },
       { label: "Face Positions", value: "Multiple SKUs per bay" },
       { label: "Cart Type", value: "Nested steel carts on rails" },
     ],
     features: [
       "Gravity-fed pallet retrieval",
       "Forklift stays in aisle (safer operation)",
       "Multiple SKUs per bay face",
       "No forklift entry into rack structure",
       "Automatic pallet presentation",
       "Faster put-away than drive-in",
     ],
     pros: [
       "Higher density than selective (50%+ more)",
       "Safer – no forklift entry into racks",
       "Multiple SKUs per bay (each lane = 1 SKU)",
       "Faster operations than drive-in",
       "Lower rack damage risk",
       "Good for medium SKU count operations",
     ],
     cons: [
       "LIFO only – limited rotation control",
       "Higher cost per position than drive-in",
       "Maximum 6 pallets deep",
       "Cart maintenance required",
       "Not ideal for date-sensitive inventory",
     ],
     useCases: [
       { industry: "Beverage Distribution", icon: Package, description: "Multiple SKUs with moderate depth needs" },
       { industry: "Manufacturing", icon: Factory, description: "Component staging and buffer storage" },
       { industry: "Cold Storage", icon: Snowflake, description: "Frozen goods with multiple product types" },
       { industry: "Consumer Goods", icon: ShoppingCart, description: "Packaged goods with medium turn rates" },
       { industry: "3PL Warehousing", icon: ArrowLeftRight, description: "Flexible multi-client storage" },
     ],
     compliance: {
       rmi: true,
       ansi: true,
       osha: true,
       seismic: true,
     },
     faqs: [
       {
         question: "How does push-back racking work?",
         answer: "Pallets are loaded onto nested carts on inclined rails. Each new pallet pushes the previous ones back. When the front pallet is removed, gravity rolls the remaining pallets forward automatically. Simple, reliable, and no power required.",
       },
       {
         question: "Why choose push-back over drive-in?",
         answer: "Push-back offers several advantages: safer operations (forklift stays in aisle), faster put-away/retrieval, less rack damage, and multiple SKUs per bay face. It's ideal when you need density but can't sacrifice too much selectivity.",
       },
       {
         question: "How many pallets deep can push-back store?",
         answer: "Typical push-back systems store 2-6 pallets deep. The depth limitation exists because each additional cart adds to the total weight the rear pallet must push. Most applications use 3-4 deep configurations for optimal performance.",
       },
       {
         question: "What maintenance does push-back require?",
         answer: "Push-back carts and rails need periodic inspection for wear, debris, and proper roll. We recommend monthly visual checks and annual professional inspections. Carts may need replacement after 10-15 years of heavy use.",
       },
       {
         question: "Is push-back racking good for cold storage?",
         answer: "Yes, push-back works well in cold storage when properly specified. Cold-rated lubricants on cart wheels and rails ensure reliable operation in freezer environments. The reduced forklift traffic in lanes also helps maintain cold chain integrity.",
       },
     ],
     relatedProducts: ["drive-in", "selective"],
     keyBenefit: "Dynamic High-Density Storage",
     densityRating: 4,
     accessibilityRating: 3,
     costEfficiency: "Best for medium SKU count with density needs",
   },
 
  cantilever: {
    id: "cantilever",
    name: "Cantilever Racking",
    slug: "cantilever",
    tagline: "Open-Front Design for Long and Bulky Items",
    shortDescription: "Adjustable arms with no front columns for lumber, pipes, and oversized goods.",
    description: "Cantilever racking features horizontal arms extending from vertical columns, creating completely open fronts with no cross-beams to obstruct loading. This design makes it perfect for storing long, bulky, or irregularly shaped items that won't fit on standard pallets. Arms are adjustable to accommodate various load sizes.",
    specs: [
      { label: "Arm Length", value: "24\" to 60\"" },
      { label: "Arm Capacity", value: "500 to 5,000 lbs per arm (configuration-specific†)" },
      { label: "Column Heights", value: "8' to 24'" },
      { label: "Arm Levels", value: "2 to 8 levels typical" },
      { label: "Column Spacing", value: "4' to 8' on center" },
      { label: "Arm Pitch", value: "0° to 4° upward" },
      { label: "Configuration", value: "Single or double-sided" },
      { label: "Construction", value: "Structural or roll-formed steel" },
    ],
    features: [
      "No front columns – completely open access",
      "Adjustable arm heights and positions",
      "Single or double-sided configurations",
      "Indoor or outdoor rated options",
      "Handles irregular and oversized loads",
      "Arms can be added or removed as needed",
    ],
    pros: [
      "Perfect for long/bulky items (lumber, pipes)",
      "100% front accessibility",
      "Easy arm height adjustment",
      "No pallet required for storage",
      "Outdoor-rated options available",
      "Handles irregular shapes easily",
    ],
    cons: [
      "Not suitable for standard pallet storage",
      "Lower density than pallet racking",
      "Requires side-loading equipment",
      "Higher cost per linear foot",
      "Limited to specific product types",
    ],
    useCases: [
      { industry: "Lumber Yards", icon: Layers, description: "Boards, plywood, and building materials" },
      { industry: "Steel Distribution", icon: Building2, description: "Pipes, tubes, bars, and structural steel" },
      { industry: "Furniture Storage", icon: Package, description: "Sofas, mattresses, and boxed furniture" },
      { industry: "Building Supply", icon: Warehouse, description: "Doors, windows, and trim materials" },
      { industry: "Manufacturing", icon: Factory, description: "Raw bar stock and extrusions" },
    ],
    compliance: {
      rmi: true,
      ansi: true,
      osha: true,
      seismic: true,
    },
    faqs: [
      {
        question: "What products work best on cantilever racking?",
        answer: "Cantilever excels at storing long items: lumber, plywood, pipes, tubing, steel bar stock, carpet rolls, furniture, and appliances. Any product that's too long for standard pallets or needs unobstructed front access is a candidate.",
      },
      {
        question: "Can cantilever racking be used outdoors?",
        answer: "Yes, we offer outdoor-rated cantilever with galvanized or powder-coated finishes. Common for lumber yards, pipe storage, and building material suppliers. Outdoor systems include provisions for load securing in wind.",
      },
      {
        question: "How do you load cantilever racking?",
        answer: "Cantilever is loaded from the side using forklifts, side-loaders, or overhead cranes. The open front allows direct placement – no maneuvering around cross-beams. For very long items, specialized side-loading equipment provides best results.",
      },
      {
          question: "What's the weight capacity of cantilever arms?",
          answer: "Arm capacity typically ranges from 500 to 5,000+ lbs per arm, depending on arm length, steel gauge, and column spacing. Actual capacity is configuration-specific and must be engineered for your loads — including deflection limits and safety factors. A LARC plaque should document rated capacities for each installation.",
        },
      {
        question: "Single-sided vs double-sided cantilever – which is better?",
        answer: "Single-sided mounts against walls (space efficient). Double-sided creates freestanding rows accessible from both sides (more capacity, needs center aisle). Choice depends on your floor plan and access requirements.",
      },
    ],
    relatedProducts: ["teardrop", "selective"],
    keyBenefit: "Open-Front Long Load Storage",
    densityRating: 2,
    accessibilityRating: 5,
    costEfficiency: "Essential for long/bulky items",
  },

  structural: {
    id: "structural",
    name: "Structural Pallet Racking",
    slug: "structural",
    tagline: "Heavy-Duty Steel Construction for Demanding Environments",
    shortDescription: "Hot-rolled structural steel channels and I-beams for maximum durability and impact resistance.",
    description: "Structural pallet racking uses hot-rolled steel channels and wide-flange beams instead of roll-formed steel. This construction method produces frames and beams with significantly higher impact resistance, making structural racking ideal for high-traffic warehouses, outdoor installations, cold storage, and environments where rack damage from forklifts is a concern.",
    specs: [
      { label: "Steel Type", value: "Hot-rolled structural C-channels & I-beams" },
      { label: "Storage Logic", value: "Direct Access (FIFO or LIFO)" },
      { label: "Upright Heights", value: "10' to 50'" },
      { label: "Beam Lengths", value: "48\" to 144\"" },
      { label: "Weight Capacity", value: "3,000 to 40,000 lbs per level (configuration-specific†)" },
      { label: "Connection", value: "Bolted beam-to-column" },
      { label: "Upright Gauge", value: "C3 to C8 structural channels" },
      { label: "Cost Tier", value: "Medium-High" },
    ],
    features: [
      "Hot-rolled steel construction for superior strength",
      "Bolted connections resist forklift impacts",
      "Individual damaged components easily replaced",
      "Outdoor and cold storage rated",
      "Heavier gauge steel resists denting",
      "Longer lifespan than roll-formed alternatives",
    ],
    pros: [
      "Superior impact resistance vs roll-formed",
      "Individual beam/column replacement without disassembly",
      "Ideal for high-traffic forklift environments",
      "Withstands outdoor weather exposure",
      "Higher weight capacities available",
      "Longer service life reduces total cost of ownership",
    ],
    cons: [
      "Higher upfront cost than roll-formed",
      "Heavier components require more labor to install",
      "Not universally cross-compatible between manufacturers",
      "Overkill for light-duty applications",
      "Bolted connections slower to adjust than teardrop clips",
    ],
    useCases: [
      { industry: "Heavy Manufacturing", icon: Factory, description: "Automotive parts, steel coils, and heavy machinery components" },
      { industry: "Cold Storage", icon: Snowflake, description: "Freezer and cooler environments where durability is critical" },
      { industry: "Outdoor Storage", icon: Warehouse, description: "Lumber yards, building supply, and open-air facilities" },
      { industry: "High-Traffic Distribution", icon: Truck, description: "Facilities with frequent forklift impacts" },
      { industry: "Food & Beverage", icon: Package, description: "Washdown environments requiring robust construction" },
    ],
    compliance: { rmi: true, ansi: true, osha: true, seismic: true },
    faqs: [
      { question: "What's the difference between structural and roll-formed racking?", answer: "Structural racking uses hot-rolled steel channels and I-beams with bolted connections, while roll-formed (teardrop) uses cold-formed sheet steel with clip-in connections. Structural is heavier, more impact-resistant, and more durable, but costs 20-40% more upfront." },
      { question: "When should I choose structural over teardrop?", answer: "Choose structural for high-traffic areas, outdoor/cold storage, very heavy loads (over 10,000 lbs per level), or when forklift damage is a recurring problem. The higher upfront cost is offset by lower maintenance and longer lifespan." },
      { question: "Can structural racking be used in drive-in configurations?", answer: "Yes, structural steel is actually preferred for drive-in racking because the forklift enters the rack structure. The bolted connections and heavier steel resist the inevitable impacts better than roll-formed alternatives." },
      { question: "Is structural racking harder to install?", answer: "Structural racking requires bolted connections rather than clip-in beams, so installation takes longer and requires tools. However, individual damaged pieces can be unbolted and replaced without disturbing adjacent sections." },
      { question: "What is the cost difference between structural and roll-formed?", answer: "Structural typically costs 20-40% more than comparable roll-formed systems. However, when factoring in reduced repair costs, longer lifespan, and less downtime from damage, many operations find structural delivers better total value." },
    ],
    relatedProducts: ["selective", "teardrop"],
    keyBenefit: "Maximum Impact Resistance",
    densityRating: 3,
    accessibilityRating: 5,
     costEfficiency: "Best value for high-traffic and demanding environments",
     mergedInto: "selective",
   },

  tubular: {
    id: "tubular",
    name: "Tubular Pallet Racking",
    slug: "tubular",
    tagline: "Round-Tube Construction for Clean and Hygienic Storage",
    shortDescription: "Closed tubular steel uprights resist debris buildup and simplify cleaning for food-grade environments.",
    description: "Tubular pallet racking uses round or square hollow steel tubes instead of open C-channel uprights. The closed-section design prevents dust, debris, and moisture from accumulating inside the uprights, making tubular racking the preferred choice for food processing, pharmaceutical, cleanroom, and other hygiene-sensitive environments.",
    specs: [
      { label: "Steel Type", value: "Closed-section round or square tubes" },
      { label: "Storage Logic", value: "Direct Access (FIFO)" },
      { label: "Upright Heights", value: "8' to 36'" },
      { label: "Beam Lengths", value: "48\" to 120\"" },
      { label: "Weight Capacity", value: "2,000 to 15,000 lbs per level (configuration-specific†)" },
      { label: "Connection", value: "Proprietary bolt or clip systems" },
      { label: "Surface Finish", value: "Powder-coated or galvanized" },
      { label: "Cost Tier", value: "Medium-High" },
    ],
    features: [
      "Closed uprights prevent dust and debris accumulation",
      "Easy to clean and sanitize",
      "Smooth surfaces reduce product contamination risk",
      "Galvanized options for washdown environments",
      "Aesthetic appearance for retail-visible storage",
      "Resistant to corrosion in humid environments",
    ],
    pros: [
      "Superior hygiene – no hidden debris traps",
      "Meets food-grade and pharmaceutical storage standards",
      "Easier cleaning and sanitation",
      "Better corrosion resistance than open-channel steel",
      "Clean, professional appearance",
      "Lower bacterial harborage risk",
    ],
    cons: [
      "Higher cost than standard roll-formed racking",
      "Proprietary connections limit cross-compatibility",
      "Lower weight capacities than structural racking",
      "Limited availability in used market",
      "Fewer accessory options compared to teardrop",
    ],
    useCases: [
      { industry: "Food Processing", icon: Package, description: "FDA-compliant storage for food products" },
      { industry: "Pharmaceutical", icon: Factory, description: "Cleanroom and controlled environments" },
      { industry: "Beverage Bottling", icon: Warehouse, description: "Hygienic storage for bottled goods" },
      { industry: "Electronics", icon: Layers, description: "Dust-sensitive component storage" },
      { industry: "Retail Display", icon: ShoppingCart, description: "Customer-facing storage areas" },
    ],
    compliance: { rmi: true, ansi: true, osha: true },
    faqs: [
      { question: "Why choose tubular over standard pallet racking?", answer: "Tubular racking's closed-section uprights prevent dust, moisture, and debris from collecting inside the frame. This makes cleaning easier and reduces contamination risk – essential for food, pharmaceutical, and cleanroom applications." },
      { question: "Is tubular racking FDA compliant?", answer: "Tubular racking supports FDA compliance for food storage when combined with proper finishes (galvanized or food-grade powder coating) and appropriate spacing. Consult your facility's food safety officer for specific requirements." },
      { question: "Can tubular racking handle heavy loads?", answer: "Tubular systems typically handle 2,000-15,000 lbs per level, which covers most food and pharmaceutical applications. For heavier loads, structural racking with sanitary coatings may be a better option." },
      { question: "How does tubular racking compare to structural?", answer: "Tubular excels in cleanliness and hygiene; structural excels in impact resistance and heavy loads. For food-grade environments without heavy forklift traffic, tubular is preferred. For demanding environments, structural with washdown coating may be better." },
      { question: "Is used tubular racking available?", answer: "Used tubular racking is less common than teardrop due to proprietary connections and smaller market share. We occasionally source used tubular systems – contact us to check current availability." },
    ],
    relatedProducts: ["selective", "structural"],
    keyBenefit: "Hygienic Clean-Storage Design",
    densityRating: 2,
    accessibilityRating: 5,
     costEfficiency: "Essential for food-grade and cleanroom storage",
     mergedInto: "selective",
   },

  "double-deep": {
    id: "double-deep",
    name: "Double-Deep Pallet Racking",
    slug: "double-deep",
    tagline: "Twice the Density Without Sacrificing Selectivity",
    shortDescription: "Two pallets deep per side, reducing aisles by up to 50% while maintaining FIFO capability.",
    description: "Double-deep pallet racking stores pallets two rows deep on each side of an aisle, effectively doubling storage density compared to single-deep selective racking. By eliminating every other aisle, you gain significant floor space. This system requires a deep-reach forklift to access the back pallet but maintains reasonable selectivity for operations with moderate SKU counts.",
    specs: [
      { label: "Storage Depth", value: "2 pallets deep per side" },
      { label: "Storage Logic", value: "FIFO with deep-reach truck" },
      { label: "Upright Heights", value: "10' to 40'" },
      { label: "Beam Lengths", value: "48\" to 120\"" },
        { label: "Weight Capacity", value: "2,000 to 20,000 lbs per level (configuration-specific†)" },
      { label: "Aisle Width", value: "10' to 14' (deep-reach truck)" },
      { label: "Density Increase", value: "Up to 40% vs. single selective" },
      { label: "Cost Tier", value: "Low-Medium" },
    ],
    features: [
      "Doubles storage density vs. single-deep selective",
      "Reduces aisle count by approximately 50%",
      "Compatible with deep-reach forklifts",
      "FIFO rotation possible with proper management",
      "Uses standard teardrop-compatible components",
      "Easy transition from single-deep systems",
    ],
    pros: [
      "Up to 40% more storage than single-deep selective",
      "Lower cost per pallet position than push-back or drive-in",
      "FIFO rotation still achievable",
      "Standard rack components (teardrop compatible)",
      "Good balance of density and selectivity",
      "Fewer aisles reduce total footprint",
    ],
    cons: [
      "Requires deep-reach forklift (higher equipment cost)",
      "Back pallet not directly accessible without moving front",
      "Wider aisles needed for deep-reach trucks",
      "50% selectivity (2 pallets per face position)",
      "Not ideal for very high SKU counts",
    ],
    useCases: [
      { industry: "Food Distribution", icon: Package, description: "Date-coded products with moderate SKU counts" },
      { industry: "Beverage Warehousing", icon: Warehouse, description: "Case goods with predictable demand patterns" },
      { industry: "Consumer Products", icon: ShoppingCart, description: "Packaged goods with medium turn rates" },
      { industry: "Cold Storage", icon: Snowflake, description: "Maximizing expensive refrigerated cubic space" },
      { industry: "3PL Operations", icon: ArrowLeftRight, description: "Multi-client storage requiring balanced density" },
    ],
    compliance: { rmi: true, ansi: true, osha: true, seismic: true },
    faqs: [
      { question: "How does double-deep compare to selective racking?", answer: "Double-deep stores two pallets deep instead of one, reducing aisles by ~50% and increasing storage by ~40%. The trade-off is you need a deep-reach forklift and lose direct access to the back pallet. It's ideal when you have 2+ pallets per SKU." },
      { question: "What forklift do I need for double-deep?", answer: "You'll need a deep-reach forklift (also called a double-deep reach truck) with extended forks or a pantograph mechanism to access the back pallet position. Standard sit-down counterbalance forklifts cannot reach the back position." },
      { question: "Is double-deep good for FIFO inventory?", answer: "Yes, with proper inventory management. Assign each face position to one SKU and refill from the back as front pallets are picked. While not as pure as single-deep selective FIFO, it's achievable with a WMS or disciplined manual system." },
      { question: "Can I convert existing selective racking to double-deep?", answer: "In many cases, yes. If your existing uprights are compatible, you can add a second row behind the first. You'll need to verify upright depth, capacity, and ensure aisle width accommodates a deep-reach truck." },
      { question: "Double-deep vs push-back – which is better?", answer: "Double-deep is lower cost and supports FIFO, but only goes 2 pallets deep and requires a special forklift. Push-back goes 2-6 deep without special equipment but is LIFO only and costs more per position. Choose based on your SKU count, budget, and rotation needs." },
    ],
    relatedProducts: ["selective", "push-back"],
    keyBenefit: "Double Density, Standard Components",
    densityRating: 3,
    accessibilityRating: 4,
    costEfficiency: "Low cost density upgrade over selective",
  },

  "drive-through": {
    id: "drive-through",
    name: "Drive-Through Pallet Racking",
    slug: "drive-through",
    tagline: "Deep Lane Storage with FIFO Inventory Flow",
    shortDescription: "Two-entry drive lane system enabling first-in, first-out rotation for date-sensitive products.",
    description: "Drive-through racking is structurally identical to drive-in racking but features entry points on both ends of each lane. This dual-entry design enables FIFO (First-In, First-Out) inventory flow — pallets are loaded from one side and retrieved from the other. Drive-through is essential for perishable goods, pharmaceuticals, and any product requiring strict date rotation.",
    specs: [
      { label: "Storage Depth", value: "4 to 10+ pallets deep" },
      { label: "Storage Logic", value: "FIFO (First-In, First-Out)" },
      { label: "Upright Heights", value: "12' to 40'" },
      { label: "Lane Width", value: "8' to 12' typical" },
      { label: "Weight Capacity", value: "2,500 to 3,000 lbs per pallet" },
      { label: "Density Increase", value: "Up to 60% vs. selective" },
      { label: "Entry Points", value: "Both ends (two-entry)" },
      { label: "Cost Tier", value: "Medium" },
    ],
    features: [
      "FIFO inventory flow through dual-entry lanes",
      "Loading from one end, retrieval from the other",
      "Same structural components as drive-in",
      "Ideal for date-sensitive product rotation",
      "Compatible with cold storage environments",
      "Can convert to drive-in by closing one end",
    ],
    pros: [
      "True FIFO rotation for perishable goods",
      "High storage density (up to 60% more than selective)",
      "Same proven structural system as drive-in",
      "Excellent for cold storage applications",
      "Reduces product spoilage and waste",
      "Flexible – can convert to drive-in if needed",
    ],
    cons: [
      "Requires aisle space on both sides of the rack",
      "Lower overall density than single-entry drive-in",
      "Entire lane typically one SKU",
      "Requires trained forklift operators",
      "More floor space needed than drive-in for same pallet count",
    ],
    useCases: [
      { industry: "Dairy & Produce", icon: Package, description: "Short-shelf-life products requiring strict date rotation" },
      { industry: "Frozen Food", icon: Snowflake, description: "Date-coded frozen goods in cold storage" },
      { industry: "Pharmaceutical", icon: Factory, description: "Lot-controlled medications and medical supplies" },
      { industry: "Beverage Distribution", icon: Truck, description: "Date-coded beverages with high throughput" },
      { industry: "Chemical Storage", icon: Warehouse, description: "FIFO-required hazardous material management" },
    ],
    compliance: { rmi: true, ansi: true, osha: true, seismic: true },
    faqs: [
      { question: "What's the difference between drive-in and drive-through?", answer: "Drive-in has one entry point (LIFO access), while drive-through has entries on both ends (FIFO). In drive-through, you load pallets from one side and retrieve from the other, ensuring first-in products are used first." },
      { question: "When should I choose drive-through over drive-in?", answer: "Choose drive-through when you need FIFO rotation – perishable foods, pharmaceuticals, chemicals, or any date-sensitive product. If LIFO is acceptable (non-perishable bulk goods), drive-in gives you higher density." },
      { question: "Is drive-through more expensive than drive-in?", answer: "The rack components cost the same. However, drive-through requires aisle space on both sides, so you need more floor space for the same number of lanes. The ROI comes from reduced spoilage and waste." },
      { question: "Can I convert drive-in to drive-through?", answer: "Yes, if your building layout allows aisles on both ends of the lanes. Simply removing the back wall allows forklift access from both sides. This is a common retrofit for operations transitioning to date-sensitive products." },
      { question: "How many pallets deep is typical for drive-through?", answer: "Most drive-through lanes are 4-10 pallets deep. Shorter lanes mean faster rotation but less density. Longer lanes increase density but slow throughput. We optimize lane depth based on your SKU velocity and rotation needs." },
    ],
    relatedProducts: ["drive-in", "pallet-flow"],
    keyBenefit: "FIFO Deep Lane Storage",
    densityRating: 4,
    accessibilityRating: 2,
    costEfficiency: "Best for perishable bulk storage requiring FIFO",
  },

  "pallet-flow": {
    id: "pallet-flow",
    name: "Pallet Flow Racking",
    slug: "pallet-flow",
    tagline: "Gravity-Fed FIFO for High-Throughput Operations",
    shortDescription: "Roller or wheel conveyors move pallets automatically from loading to picking face.",
    description: "Pallet flow racking (also called gravity flow or live storage) uses inclined roller or wheel conveyors within the rack structure. Pallets are loaded at the high end and flow by gravity to the picking face at the low end. This creates automatic FIFO rotation without forklift entry into the rack, making it ideal for high-throughput, date-sensitive operations.",
    specs: [
      { label: "Storage Depth", value: "3 to 20+ pallets deep" },
      { label: "Storage Logic", value: "FIFO (Gravity-fed, automatic)" },
      { label: "Upright Heights", value: "12' to 40'" },
      { label: "Lane Width", value: "Standard pallet width" },
      { label: "Weight Capacity", value: "2,000 to 2,500 lbs per pallet (configuration-specific†)" },
      { label: "Flow Speed", value: "Controlled by speed regulators/brakes" },
      { label: "Lane Pitch", value: "3% to 5% grade" },
      { label: "Cost Tier", value: "High" },
    ],
    features: [
      "Automatic FIFO rotation via gravity conveyors",
      "No forklift entry into rack structure",
      "Separate loading and picking aisles",
      "Speed controllers prevent runaway pallets",
      "Maximizes cube utilization with deep lanes",
      "Reduces forklift travel time significantly",
    ],
    pros: [
      "Perfect automatic FIFO rotation",
      "Very high storage density (up to 70% more than selective)",
      "Reduced forklift travel and labor costs",
      "Safe – forklifts never enter rack structure",
      "Ideal for high-volume, date-sensitive products",
      "Separates loading and picking operations",
    ],
    cons: [
      "High initial cost (rollers/wheels/brakes)",
      "Regular conveyor maintenance required",
      "One SKU per lane",
      "Complex installation vs. static racking",
      "Not cost-effective for slow-moving SKUs",
    ],
    useCases: [
      { industry: "Food & Beverage", icon: Package, description: "High-volume FIFO distribution" },
      { industry: "Dairy Processing", icon: Snowflake, description: "Short-shelf-life products with strict rotation" },
      { industry: "Pharmaceutical Distribution", icon: Factory, description: "Lot-tracked medications requiring FIFO" },
      { industry: "High-Volume Distribution", icon: Truck, description: "Fast-moving consumer goods" },
      { industry: "Bottling Plants", icon: Warehouse, description: "Date-coded beverages and liquids" },
    ],
    compliance: { rmi: true, ansi: true, osha: true, seismic: true },
    faqs: [
      { question: "How does pallet flow racking work?", answer: "Pallets are loaded at the high end of inclined roller or wheel lanes. Gravity moves them to the picking face at the low end. Speed controllers and brakes regulate flow, and separators ensure pallets don't push against each other." },
      { question: "Pallet flow vs push-back – what's the difference?", answer: "Pallet flow is FIFO (first loaded = first picked), while push-back is LIFO (last loaded = first picked). Pallet flow uses gravity rollers and has separate load/pick aisles. Push-back uses nested carts and works from one aisle. Choose based on rotation needs." },
      { question: "How many pallets deep can pallet flow store?", answer: "Pallet flow lanes can be 3-20+ pallets deep, much deeper than push-back (max 6). Deeper lanes increase density but cost more per position. Most installations use 6-12 deep for optimal balance of density and cost." },
      { question: "What maintenance does pallet flow require?", answer: "Roller and wheel conveyors need periodic inspection for debris, worn bearings, and proper brake function. Speed controllers may need adjustment. We recommend quarterly inspections and annual professional maintenance for optimal performance." },
      { question: "Is pallet flow worth the higher cost?", answer: "For high-volume, date-sensitive operations, yes. Reduced labor (less forklift travel), automatic FIFO (less spoilage), and high density (less floor space) typically deliver ROI within 18-36 months for qualifying operations." },
    ],
    relatedProducts: ["push-back", "drive-through"],
    keyBenefit: "Automatic FIFO Gravity Flow",
    densityRating: 5,
    accessibilityRating: 2,
    costEfficiency: "Premium system for high-volume FIFO operations",
  },

  vna: {
    id: "vna",
    name: "Very Narrow Aisle (VNA) Racking",
    slug: "vna",
    tagline: "Maximum Floor Utilization with Full Pallet Selectivity",
    shortDescription: "Aisles as narrow as 5 feet with 100% pallet access using specialized turret trucks.",
    description: "Very Narrow Aisle (VNA) racking uses the same selective rack structure but reduces aisle widths to as little as 5-6 feet by using specialized turret trucks or swing-reach forklifts. This achieves up to 45% more pallet positions than conventional selective in the same footprint while maintaining 100% selectivity — the best of both worlds for space-constrained warehouses.",
    specs: [
      { label: "Aisle Width", value: "5' to 6' (vs. 10-12' conventional)" },
      { label: "Storage Logic", value: "Direct Access (100% selectivity)" },
      { label: "Upright Heights", value: "20' to 50'+" },
      { label: "Beam Lengths", value: "48\" to 120\"" },
      { label: "Weight Capacity", value: "2,000 to 25,000 lbs per level (configuration-specific†)" },
      { label: "Density Increase", value: "Up to 45% vs. wide-aisle selective" },
      { label: "Equipment Required", value: "Turret truck or swing-reach truck" },
      { label: "Cost Tier", value: "High (rack + equipment)" },
    ],
    features: [
      "Aisles reduced to 5-6 feet wide",
      "100% pallet selectivity maintained",
      "Guide rails or wire guidance systems",
      "Heights up to 50+ feet possible",
      "Compatible with WMS integration",
      "Floor must be super-flat (F-min 100)",
    ],
    pros: [
      "Up to 45% more storage than wide-aisle selective",
      "100% selectivity – access any pallet instantly",
      "Best density-to-selectivity ratio available",
      "Ideal for expensive real estate locations",
      "Reduces need for facility expansion",
      "Full FIFO capability maintained",
    ],
    cons: [
      "Requires specialized (expensive) turret trucks",
      "Super-flat floor required (costly if not existing)",
      "Guide rail or wire guidance installation needed",
      "Higher equipment operator training required",
      "Limited equipment redundancy (one truck per aisle)",
    ],
    useCases: [
      { industry: "Urban Distribution Centers", icon: Building2, description: "Maximizing expensive urban real estate" },
      { industry: "E-Commerce Fulfillment", icon: ShoppingCart, description: "High SKU count with space constraints" },
      { industry: "Automotive Parts", icon: Factory, description: "Thousands of SKUs requiring direct access" },
      { industry: "Electronics Distribution", icon: Layers, description: "High-value, high-SKU inventory" },
      { industry: "Pharmaceutical Warehousing", icon: Package, description: "Lot-tracked inventory in controlled space" },
    ],
    compliance: { rmi: true, ansi: true, osha: true, seismic: true },
    faqs: [
      { question: "How narrow are VNA aisles?", answer: "VNA aisles are typically 5-6 feet wide, compared to 10-12 feet for conventional selective racking. This is possible because turret trucks don't need to turn within the aisle – they rotate the fork head instead of the entire vehicle." },
      { question: "What equipment do I need for VNA?", answer: "You'll need turret trucks (man-up or man-down) or swing-reach trucks. These are guided by rail or wire embedded in the floor. They're more expensive than standard forklifts but the space savings typically justify the investment." },
      { question: "Does VNA require special flooring?", answer: "Yes, VNA requires super-flat floors (typically F-min 100 or better). At heights above 30 feet, even minor floor imperfections translate to significant sway at the top of the mast. Floor grinding or resurfacing may be needed." },
      { question: "VNA vs. double-deep – which saves more space?", answer: "VNA typically saves 30-45% vs. conventional aisles while maintaining 100% selectivity. Double-deep saves ~40% but with only 50% selectivity. VNA is better for high-SKU operations; double-deep is better for lower-SKU operations." },
      { question: "Can I convert existing selective racking to VNA?", answer: "Often yes, if your floor is flat enough and ceiling height allows. The main changes are adding guide rails, narrowing aisles, and switching to turret trucks. We assess feasibility during a free site survey." },
    ],
    relatedProducts: ["selective", "double-deep"],
    keyBenefit: "Maximum Density + Full Selectivity",
    densityRating: 4,
    accessibilityRating: 5,
    costEfficiency: "Best ROI when real estate costs are high",
  },

  mobile: {
    id: "mobile",
    name: "Mobile Pallet Racking",
    slug: "mobile",
    tagline: "Rack Rows That Move to Eliminate Wasted Aisle Space",
    shortDescription: "Motorized bases move entire rack rows, reducing aisles from many to one working aisle.",
    description: "Mobile pallet racking (also called movable or compacting racking) mounts standard pallet racks on motorized carriages that travel on floor-mounted rails. By eliminating all but one working aisle, mobile racking can increase storage capacity by up to 80% compared to static selective racking. When a specific aisle is needed, the rows slide apart automatically.",
    specs: [
      { label: "Base Type", value: "Motorized carriages on floor rails" },
      { label: "Storage Logic", value: "Direct Access (FIFO capable)" },
      { label: "Upright Heights", value: "12' to 40'" },
      { label: "Beam Lengths", value: "48\" to 120\"" },
      { label: "Weight Capacity", value: "2,000 to 20,000 lbs per level (configuration-specific†)" },
      { label: "Density Increase", value: "Up to 80% vs. static selective" },
      { label: "Aisle Configuration", value: "One working aisle that shifts" },
      { label: "Cost Tier", value: "High" },
    ],
    features: [
      "Motorized bases move entire rack rows",
      "Only one working aisle required at a time",
      "Full selectivity to every pallet position",
      "Safety sensors prevent row movement during access",
      "Remote and automated control options",
      "Can be integrated with WMS systems",
    ],
    pros: [
      "Up to 80% more storage than static selective",
      "100% selectivity maintained",
      "Ideal for space-constrained facilities",
      "Full FIFO capability",
      "Can delay or avoid facility expansion",
      "Safety interlocks protect personnel",
    ],
    cons: [
      "Highest cost per position of any pallet racking",
      "Floor rail installation required",
      "Slower access than static racking (wait for row movement)",
      "Mechanical maintenance required for carriages",
      "Not suitable for very high throughput operations",
    ],
    useCases: [
      { industry: "Cold Storage", icon: Snowflake, description: "Maximizing expensive refrigerated/frozen space" },
      { industry: "Archive & Document Storage", icon: Layers, description: "Low-access records requiring maximum density" },
      { industry: "Wine & Spirits", icon: Package, description: "Climate-controlled high-value inventory" },
      { industry: "Military & Government", icon: Building2, description: "Secure storage in space-limited facilities" },
      { industry: "Pharmaceutical", icon: Factory, description: "Controlled-access storage for medications" },
    ],
    compliance: { rmi: true, ansi: true, osha: true, seismic: true },
    faqs: [
      { question: "How does mobile racking work?", answer: "Standard pallet racks are mounted on motorized carriages that ride on floor rails. When you need access to a specific aisle, you push a button (or the WMS sends a command) and the rows slide apart to create a working aisle at that location." },
      { question: "Is mobile racking safe?", answer: "Yes, with multiple safety systems: floor-level light curtains, pressure-sensitive safety edges, photoelectric sensors, and emergency stops. Rows will not move if a person or equipment is detected in an aisle." },
      { question: "Where does mobile racking make the most sense?", answer: "Mobile racking delivers the best ROI in cold storage (where cubic space is most expensive), space-constrained urban facilities, and operations where building expansion is impossible or prohibitively expensive." },
      { question: "How fast do the rows move?", answer: "Typical carriage speed is 20-40 feet per minute. For a 40-foot deep row, opening an aisle takes 1-2 minutes. This makes mobile racking best for moderate throughput operations – not ideal for high-speed pick-and-ship environments." },
      { question: "Mobile racking vs drive-in – which is better?", answer: "Mobile gives 100% selectivity and FIFO capability; drive-in is LIFO with limited selectivity. Mobile costs more but is far more flexible. Choose mobile when you need density + selectivity; choose drive-in when LIFO is acceptable and budget is tighter." },
    ],
    relatedProducts: ["selective", "drive-in"],
    keyBenefit: "Compacting Density + Full Selectivity",
    densityRating: 5,
    accessibilityRating: 4,
    costEfficiency: "Best for cold storage and space-constrained facilities",
  },

  shuttle: {
    id: "shuttle",
    name: "Shuttle Racking",
    slug: "shuttle",
    tagline: "Semi-Automated Deep Lane Storage for High Throughput",
    shortDescription: "Battery-powered shuttle carts move pallets within deep lanes, eliminating forklift entry.",
    description: "Shuttle racking uses battery-powered shuttle carts (also called radio shuttles or satellite systems) to transport pallets within deep rack lanes. A forklift places the pallet at the lane entrance and the shuttle carries it to the next available position. This semi-automated approach combines the density of drive-in with the speed and safety of keeping forklifts out of the rack structure.",
    specs: [
      { label: "Storage Depth", value: "10 to 40+ pallets deep" },
      { label: "Storage Logic", value: "FIFO or LIFO (configurable)" },
      { label: "Upright Heights", value: "12' to 45'" },
      { label: "Lane Width", value: "Standard pallet width + clearance" },
      { label: "Weight Capacity", value: "2,000 to 3,500 lbs per pallet (configuration-specific†)" },
      { label: "Shuttle Speed", value: "2-3 ft/sec loaded" },
      { label: "Battery Life", value: "8-12 hours per charge" },
      { label: "Cost Tier", value: "High (rack + shuttle units)" },
    ],
    features: [
      "Battery-powered shuttle carts move pallets automatically",
      "Forklift never enters the rack structure",
      "FIFO or LIFO operation selectable",
      "Deeper lanes than drive-in (40+ pallets)",
      "Remote control operation via tablet or WMS",
      "Shuttles can be shared across multiple lanes",
    ],
    pros: [
      "Extremely high storage density",
      "Much safer than drive-in (no forklift entry)",
      "Faster throughput than traditional drive-in",
      "Significantly reduced rack damage",
      "FIFO or LIFO flexibility",
      "Lower labor costs than manual drive-in",
    ],
    cons: [
      "High initial investment (shuttle units + racking)",
      "Battery charging/maintenance required",
      "One shuttle per active lane needed",
      "Technical support for shuttle maintenance",
      "Entire lane still limited to one SKU",
    ],
    useCases: [
      { industry: "Cold Storage", icon: Snowflake, description: "Deep freezer storage minimizing worker exposure" },
      { industry: "Food Manufacturing", icon: Factory, description: "Buffer storage between production and shipping" },
      { industry: "Beverage Distribution", icon: Package, description: "High-volume single-SKU deep storage" },
      { industry: "E-Commerce", icon: ShoppingCart, description: "Automated buffer for high-volume fulfillment" },
      { industry: "Retail Distribution", icon: Truck, description: "Seasonal and promotional stock staging" },
    ],
    compliance: { rmi: true, ansi: true, osha: true, seismic: true },
    faqs: [
      { question: "How does shuttle racking work?", answer: "A forklift places a pallet at the lane entrance. A battery-powered shuttle cart picks it up and moves it to the next open position deep within the lane. For retrieval, the process reverses. One shuttle can serve multiple lanes by being transferred via forklift." },
      { question: "Shuttle vs drive-in – which is better?", answer: "Shuttle eliminates forklift entry into racks (safer, less damage), enables deeper lanes (40+ vs 10-12), and offers FIFO or LIFO flexibility. Drive-in is simpler and cheaper. Choose shuttle for cold storage, high-volume, or when rack damage is a problem." },
      { question: "How many shuttles do I need?", answer: "You need one shuttle per simultaneously active lane. Since lanes aren't all active at once, most operations use 1 shuttle per 4-8 lanes. The shuttle is transferred between lanes by forklift when needed." },
      { question: "Can shuttle racking do FIFO?", answer: "Yes, with dual-entry lanes (loading from one end, retrieval from the other). The shuttle can operate in both directions. For LIFO, single-entry lanes work. This flexibility is a major advantage over drive-in." },
      { question: "What happens if a shuttle breaks down?", answer: "The pallet inventory remains safely in the lane. A replacement shuttle can be deployed immediately. Most facilities keep one spare shuttle on hand. Routine battery swaps and preventive maintenance minimize downtime." },
    ],
    relatedProducts: ["drive-in", "pallet-flow"],
    keyBenefit: "Semi-Automated Deep Lane Storage",
    densityRating: 5,
    accessibilityRating: 2,
    costEfficiency: "Best for deep cold storage and high-volume bulk operations",
  },

  "asrs": {
    id: "asrs",
    name: "AS/RS Pallet System",
    slug: "asrs",
    tagline: "Fully Automated Storage and Retrieval for Maximum Efficiency",
    shortDescription: "Automated cranes store and retrieve pallets without human-operated forklifts.",
    description: "Automated Storage and Retrieval Systems (AS/RS) use computer-controlled cranes or shuttles to automatically store and retrieve pallets from high-density rack structures. By eliminating forklifts and human operators within the storage area, AS/RS maximizes vertical space utilization, throughput accuracy, and safety while reducing labor costs.",
    specs: [
      { label: "Heights", value: "40' to 120'+" },
      { label: "Storage Logic", value: "Fully automated (FIFO, LIFO, or random)" },
      { label: "Aisle Width", value: "5' to 7' (crane-dependent)" },
      { label: "Weight Capacity", value: "Up to 5,000 lbs per pallet (configuration-specific†)" },
      { label: "Throughput", value: "20 to 60+ pallets/hour per aisle" },
      { label: "Accuracy", value: "99.9%+ inventory accuracy" },
      { label: "Automation Level", value: "Fully automated" },
      { label: "Cost Tier", value: "Automation (highest)" },
    ],
    features: [
      "Computer-controlled stacker cranes or shuttles",
      "No human operators in storage aisles",
      "Heights up to 120+ feet",
      "99.9%+ inventory accuracy",
      "Full WMS/ERP integration",
      "24/7 operation capability",
    ],
    pros: [
      "Highest possible storage density",
      "Eliminates forklift labor costs",
      "Near-perfect inventory accuracy",
      "Maximum vertical space utilization (120'+)",
      "24/7 unmanned operation possible",
      "Safest storage system (no human exposure)",
    ],
    cons: [
      "Highest capital investment of any storage system",
      "Long lead times (12-24 months typical)",
      "Requires specialized maintenance technicians",
      "Single point of failure risk per aisle crane",
      "Difficult to reconfigure once installed",
    ],
    useCases: [
      { industry: "Pharmaceutical Distribution", icon: Factory, description: "Lot-tracked, temperature-controlled, fully automated" },
      { industry: "Frozen Food Distribution", icon: Snowflake, description: "Eliminating worker exposure to extreme cold" },
      { industry: "Automotive Manufacturing", icon: Truck, description: "Just-in-time component delivery to production lines" },
      { industry: "E-Commerce Mega Centers", icon: ShoppingCart, description: "Ultra-high-volume order fulfillment" },
      { industry: "Aerospace & Defense", icon: Building2, description: "Secure, tracked storage for high-value components" },
    ],
    compliance: { rmi: true, ansi: true, osha: true, seismic: true },
    faqs: [
      { question: "What is an AS/RS system?", answer: "AS/RS (Automated Storage and Retrieval System) uses computer-controlled cranes or shuttle vehicles to automatically place and retrieve pallets from a high-density rack structure. No forklifts or human operators enter the storage aisles." },
      { question: "How much does an AS/RS cost?", answer: "AS/RS systems typically range from $2-20+ million depending on size, height, throughput requirements, and complexity. ROI typically takes 5-10 years but can be faster for operations with high labor costs, expensive real estate, or 24/7 operations." },
      { question: "When is AS/RS justified?", answer: "AS/RS makes sense when: labor costs are very high or workers are unavailable, real estate is extremely expensive, throughput accuracy must be 99.9%+, cold storage eliminates human exposure, or 24/7 operation is needed without additional shifts." },
      { question: "How reliable are AS/RS systems?", answer: "Modern AS/RS systems achieve 98%+ uptime with proper maintenance. Most have redundancy features – if one crane fails, adjacent cranes can cover. Preventive maintenance contracts typically guarantee specific uptime levels." },
      { question: "Can AS/RS be added to an existing building?", answer: "In some cases, yes – if ceiling height and floor capacity are sufficient. However, most AS/RS installations are designed as part of new construction or major building modifications to optimize height, floor strength, and crane rail alignment." },
    ],
    relatedProducts: ["vna", "shuttle"],
    keyBenefit: "Fully Automated Pallet Storage",
    densityRating: 5,
    accessibilityRating: 5,
    costEfficiency: "Highest ROI for large-scale 24/7 operations",
  },

  "stack-racks": {
    id: "stack-racks",
    name: "Stack Racks",
    slug: "stack-racks",
    tagline: "Portable, Stackable Frames for Flexible Storage",
    shortDescription: "Freestanding metal frames that stack on top of each other, no permanent installation required.",
    description: "Stack racks (also called stackable racks or portable racking) are freestanding metal frames that interlock and stack vertically. Unlike permanent racking systems, stack racks require no anchoring, assembly, or installation. They're perfect for seasonal overflow, temporary storage, and products that need both indoor and outdoor flexibility.",
    specs: [
      { label: "Configuration", value: "Stackable 2-5 units high" },
      { label: "Storage Logic", value: "Direct Access (top units)" },
      { label: "Base Dimensions", value: "48\" x 48\" to 60\" x 60\" typical" },
      { label: "Load Capacity", value: "2,000 to 4,000 lbs per unit (configuration-specific†)" },
      { label: "Stacking Height", value: "2 to 5 tiers" },
      { label: "Material", value: "Welded tubular or structural steel" },
      { label: "Mobility", value: "Forklift-portable" },
      { label: "Cost Tier", value: "Low" },
    ],
    features: [
      "No installation required – just stack and use",
      "Forklift portable – move anywhere in the facility",
      "Nest or fold flat when not in use",
      "Indoor and outdoor use",
      "No floor anchoring required",
      "Customizable to fit specific products",
    ],
    pros: [
      "Zero installation time and cost",
      "Completely portable and relocatable",
      "Nest/fold flat for compact storage when empty",
      "Ideal for seasonal or temporary storage",
      "No building permits or floor anchoring needed",
      "Low cost per unit",
    ],
    cons: [
      "Lower storage density than permanent racking",
      "Limited stacking height (typically 5 tiers max)",
      "Not suitable for high-rise warehouse applications",
      "Top-tier access only (lower tiers blocked)",
      "Less stable than anchored rack systems",
    ],
    useCases: [
      { industry: "Seasonal Retail", icon: ShoppingCart, description: "Holiday merchandise overflow storage" },
      { industry: "Outdoor Yards", icon: Warehouse, description: "Building materials and heavy goods" },
      { industry: "Manufacturing", icon: Factory, description: "Work-in-progress and staging areas" },
      { industry: "Agriculture", icon: Package, description: "Harvest season temporary storage" },
      { industry: "Logistics", icon: Truck, description: "Flexible warehouse reconfiguration" },
    ],
    compliance: { rmi: true, ansi: true, osha: true },
    faqs: [
      { question: "How high can stack racks be stacked?", answer: "Typically 2-5 units high, depending on load weight and base design. Always follow the manufacturer's stacking limit. On uneven surfaces or outdoors, reduce stacking height for stability." },
      { question: "Do stack racks need to be anchored?", answer: "No. Stack racks are freestanding and use interlocking posts or pins for stability. This is their primary advantage – instant deployment without floor modifications. However, they should always be placed on level, hard surfaces." },
      { question: "Can stack racks be used outdoors?", answer: "Yes. Galvanized or powder-coated stack racks are designed for outdoor use. They're common in lumber yards, building supply stores, and agricultural operations. Ensure ground surfaces are level and firm." },
      { question: "What's the difference between stack racks and post pallets?", answer: "Stack racks have four posts and typically fold flat when empty. Post pallets have removable corner posts on a pallet base. Stack racks are generally more durable and stackable to greater heights, while post pallets are lighter and cheaper." },
      { question: "How do stack racks compare to permanent racking?", answer: "Stack racks are portable, require no installation, and have zero anchor points. They cost less per unit but have lower density and height limits. Use stack racks for flexible/temporary needs and permanent racking for fixed, high-density storage." },
    ],
    relatedProducts: ["post-pallets", "selective"],
    keyBenefit: "Portable No-Install Storage",
    densityRating: 2,
    accessibilityRating: 3,
    costEfficiency: "Lowest cost flexible storage solution",
  },

  "post-pallets": {
    id: "post-pallets",
    name: "Post Pallet Racks",
    slug: "post-pallets",
    tagline: "Removable Post Frames for Lightweight Stackable Storage",
    shortDescription: "Pallet-base frames with removable corner posts for stackable, nestable storage.",
    description: "Post pallet racks feature a pallet-sized base with removable corner posts that support stacking multiple units vertically. When the posts are removed, the bases nest together for compact storage. Post pallets are lighter and less expensive than stack racks, making them ideal for light to medium-duty temporary storage and shipping applications.",
    specs: [
      { label: "Configuration", value: "Stackable 2-4 units high" },
      { label: "Storage Logic", value: "Direct Access (top unit)" },
      { label: "Base Dimensions", value: "48\" x 40\" to 48\" x 48\" typical" },
      { label: "Load Capacity", value: "1,000 to 2,500 lbs per unit (configuration-specific†)" },
      { label: "Post Height", value: "36\" to 48\" per tier" },
      { label: "Material", value: "Tubular steel posts, sheet metal base" },
      { label: "Mobility", value: "Forklift or pallet jack portable" },
      { label: "Cost Tier", value: "Low" },
    ],
    features: [
      "Removable corner posts for flat nesting",
      "Lighter weight than stack racks",
      "Pallet jack compatible (no forklift required for ground level)",
      "Posts socket into base corners for quick assembly",
      "Rust-resistant powder coating available",
      "Custom sizes available for specific products",
    ],
    pros: [
      "Lowest cost stackable storage option",
      "Posts remove for flat nesting (saves 70% space when empty)",
      "Lightweight – easy to handle and transport",
      "Works with pallet jacks (no forklift needed at ground level)",
      "No tools required for assembly/disassembly",
      "Ideal for shipping and interplant transfers",
    ],
    cons: [
      "Lower load capacity than stack racks",
      "Limited stacking height (typically 3-4 tiers)",
      "Posts can get lost or damaged separately",
      "Less rigid than welded stack racks",
      "Not suitable for permanent high-density storage",
    ],
    useCases: [
      { industry: "Automotive Parts", icon: Factory, description: "Interplant parts transfer and staging" },
      { industry: "Retail Backstock", icon: ShoppingCart, description: "Seasonal overflow and promotional displays" },
      { industry: "Packaging & Paper", icon: Layers, description: "Stacking corrugated and paper products" },
      { industry: "Textile Storage", icon: Package, description: "Fabric rolls and garment storage" },
      { industry: "Light Manufacturing", icon: Warehouse, description: "Work-in-progress and component staging" },
    ],
    compliance: { rmi: true, ansi: true, osha: true },
    faqs: [
      { question: "How are post pallets different from stack racks?", answer: "Post pallets have removable corner posts on a pallet base and are lighter duty. Stack racks have welded frames and are heavier duty. Post pallets are cheaper and nest flatter when empty, but have lower weight capacity and stacking limits." },
      { question: "Can post pallets be shipped with products inside?", answer: "Yes, post pallets are commonly used for interplant and intercompany product transfers. The products stay in the post pallet during shipping and storage, reducing handling. Posts keep the load contained and enable stacking in transit." },
      { question: "How many post pallets can I stack?", answer: "Typically 2-4 tiers depending on load weight. Always follow the rated capacity. For stability, ensure all posts are fully seated in their sockets and loads are evenly distributed within each unit." },
      { question: "Do I need a forklift for post pallets?", answer: "Post pallets can be moved by pallet jack at ground level. For stacking and unstacking, a forklift is needed. Their standard pallet-base dimensions make them compatible with all standard material handling equipment." },
      { question: "Are custom sizes available?", answer: "Yes, post pallets can be manufactured to custom dimensions for specific product sizes. Custom post heights, base dimensions, and load ratings are available. Minimum order quantities typically apply for custom specifications." },
    ],
    relatedProducts: ["stack-racks", "selective"],
    keyBenefit: "Lightweight Nestable Storage",
    densityRating: 1,
    accessibilityRating: 3,
    costEfficiency: "Most economical temporary storage option",
  },

  "clad-rack": {
    id: "clad-rack",
    name: "Rack-Supported Buildings (Clad Rack)",
    slug: "clad-rack",
    tagline: "The Rack IS the Building Structure",
    shortDescription: "Racking system serves as both storage and building framework, eliminating separate construction.",
    description: "Rack-supported buildings (also called clad rack or rack-clad structures) use the racking system itself as the building's structural framework. Wall cladding and roofing attach directly to the rack uprights, eliminating the need for a separate steel building structure. This approach reduces construction costs by 20-30% and maximizes usable height for storage.",
    specs: [
      { label: "Heights", value: "40' to 130'" },
      { label: "Storage Logic", value: "Varies (typically AS/RS or shuttle)" },
      { label: "Construction", value: "Rack uprights = building columns" },
      { label: "Cladding", value: "Insulated metal panels, PUR/PIR" },
      { label: "Weight Capacity", value: "Engineered per project" },
      { label: "Temperature", value: "Ambient, cooler, or freezer" },
      { label: "Automation", value: "Usually fully automated" },
      { label: "Cost Tier", value: "Automation (major capital project)" },
    ],
    features: [
      "Racking uprights serve as building columns",
      "Wall and roof panels attach directly to rack",
      "No separate building steel required",
      "Heights up to 130 feet possible",
      "Optimized for cold storage applications",
      "Usually paired with AS/RS automation",
    ],
    pros: [
      "20-30% construction cost savings vs. conventional building + racking",
      "Maximum height utilization (up to 130')",
      "Faster construction timeline",
      "Perfect cube utilization – no wasted space",
      "Ideal for freezer/cold storage (less volume to cool)",
      "Integrated structural engineering",
    ],
    cons: [
      "Major capital project (multi-million dollar)",
      "Cannot be reconfigured once built",
      "Requires specialized engineering and construction",
      "Long lead times (18-36 months)",
      "Not suitable for operations needing layout flexibility",
    ],
    useCases: [
      { industry: "Frozen Food Distribution", icon: Snowflake, description: "Minimizing frozen cubic footage to cool" },
      { industry: "Large-Scale Distribution", icon: Truck, description: "National distribution centers requiring maximum density" },
      { industry: "Beverage Companies", icon: Package, description: "High-volume single-product storage" },
      { industry: "Pharmaceutical Cold Chain", icon: Factory, description: "Temperature-controlled pharmaceutical distribution" },
      { industry: "Retail Distribution", icon: Building2, description: "Regional mega-distribution centers" },
    ],
    compliance: { rmi: true, ansi: true, osha: true, seismic: true },
    faqs: [
      { question: "What is a rack-supported building?", answer: "A rack-supported (clad rack) building uses the storage rack structure as the building's framework. Walls and roof panels attach directly to the rack uprights. The rack literally IS the building – there's no separate steel structure, saving significant construction cost." },
      { question: "How much does a clad rack building cost?", answer: "Clad rack buildings are major capital projects typically ranging from $5-50+ million depending on size, automation level, and temperature requirements. However, they save 20-30% compared to building a conventional warehouse and installing racking separately." },
      { question: "Are clad rack buildings only for large operations?", answer: "Clad rack is most cost-effective for facilities over 50,000 pallet positions. Below that threshold, conventional construction with separate racking is usually more economical. The sweet spot is large, high-bay, automated facilities." },
      { question: "Can a clad rack building be expanded?", answer: "Expansion is possible but must be planned during initial design. The structural engineering must account for future additions. Unlike conventional warehouses where you can simply add racking, clad rack expansion involves building modification." },
      { question: "Is clad rack only for cold storage?", answer: "No, but cold storage is where clad rack delivers the highest ROI. By minimizing the building volume, you reduce the cubic footage that needs refrigeration. Clad rack is also used for ambient distribution centers where height maximization is a priority." },
    ],
    relatedProducts: ["asrs", "shuttle"],
    keyBenefit: "Building + Storage Combined",
    densityRating: 5,
    accessibilityRating: 4,
    costEfficiency: "Most efficient for large-scale new construction",
  },

  "multi-deep-asrs": {
    id: "multi-deep-asrs",
    name: "Multi-Deep AS/RS",
    slug: "multi-deep-asrs",
    tagline: "Automated Deep-Lane Storage for Maximum Cube Utilization",
    shortDescription: "AS/RS cranes with satellite shuttles accessing multiple pallets deep per lane.",
    description: "Multi-deep AS/RS combines traditional automated cranes with satellite shuttle vehicles to access pallets stored multiple positions deep within a lane. While standard AS/RS stores pallets single-deep, multi-deep configurations can reach 4-10+ pallets deep, dramatically increasing storage density while maintaining full automation.",
    specs: [
      { label: "Depth", value: "4 to 10+ pallets deep per lane" },
      { label: "Storage Logic", value: "Automated FIFO or LIFO" },
      { label: "Heights", value: "40' to 100'+" },
      { label: "Weight Capacity", value: "Up to 3,500 lbs per pallet (configuration-specific†)" },
      { label: "Throughput", value: "15 to 40 pallets/hour per aisle" },
      { label: "Automation Level", value: "Fully automated (crane + satellite)" },
      { label: "Satellite Type", value: "Battery-powered shuttle within lanes" },
      { label: "Cost Tier", value: "Automation (highest)" },
    ],
    features: [
      "Automated crane + satellite shuttle combination",
      "4-10+ pallets deep per lane",
      "Higher density than single-deep AS/RS",
      "FIFO or LIFO operation programmable",
      "Fewer aisles needed than single-deep AS/RS",
      "Ideal for bulk storage of homogeneous products",
    ],
    pros: [
      "Maximum possible automated storage density",
      "Fewer crane aisles needed (lower equipment cost)",
      "Fully automated – zero forklift labor",
      "24/7 unmanned operation",
      "Reduced building footprint vs. single-deep AS/RS",
      "Ideal for cold storage automation",
    ],
    cons: [
      "Highest capital cost of any storage system",
      "Complex integration of crane + shuttle systems",
      "Lower throughput per aisle than single-deep AS/RS",
      "Lane limited to one SKU",
      "Requires specialized maintenance team",
    ],
    useCases: [
      { industry: "Frozen Distribution", icon: Snowflake, description: "Bulk frozen food in automated cold storage" },
      { industry: "Beverage Manufacturing", icon: Package, description: "High-volume finished goods buffer" },
      { industry: "Paper & Packaging", icon: Layers, description: "Automated bulk storage of rolls and sheets" },
      { industry: "Chemical Manufacturing", icon: Factory, description: "Automated hazardous material storage" },
      { industry: "National Distribution", icon: Truck, description: "Mega-center automated storage" },
    ],
    compliance: { rmi: true, ansi: true, osha: true, seismic: true },
    faqs: [
      { question: "How does multi-deep AS/RS differ from standard AS/RS?", answer: "Standard AS/RS stores pallets single-deep (one pallet per position). Multi-deep uses satellite shuttles within the crane aisle to access pallets 4-10+ deep per lane. This dramatically increases density but reduces throughput per aisle." },
      { question: "When does multi-deep AS/RS make sense?", answer: "Multi-deep AS/RS is ideal when you have high-volume, low-SKU products requiring maximum density with full automation. Common applications: frozen food, beverage, paper/packaging where you store many pallets of the same product." },
      { question: "How does the satellite shuttle work?", answer: "The AS/RS crane delivers a pallet and satellite to the lane entrance. The satellite carries the pallet to the next open position deep within the lane. For retrieval, the satellite fetches the target pallet and returns it to the crane." },
      { question: "Is multi-deep AS/RS reliable?", answer: "Modern multi-deep systems achieve 97%+ uptime. The satellite shuttles are simple, battery-powered devices with few moving parts. The AS/RS crane is the same proven technology used in single-deep systems. Redundancy is built into most designs." },
      { question: "What's the ROI timeline for multi-deep AS/RS?", answer: "Typically 7-15 years depending on labor savings, real estate costs, and throughput requirements. In cold storage where labor is scarce and cubic space costs $200+/pallet/year, ROI can be faster (5-8 years)." },
    ],
    relatedProducts: ["asrs", "shuttle"],
    keyBenefit: "Maximum Automated Density",
    densityRating: 5,
    accessibilityRating: 3,
     costEfficiency: "Most dense automation for bulk storage",
     mergedInto: "asrs",
   },

  "hybrid-beam-rail": {
    id: "hybrid-beam-rail",
    name: "Hybrid Beam + Rail Systems",
    slug: "hybrid-beam-rail",
    tagline: "Combining Selective Access with Deep Lane Density",
    shortDescription: "Mixed racking combining beam levels for picking with rail levels for deep storage.",
    description: "Hybrid beam + rail systems combine conventional beam-level storage (for selective picking) with rail-level storage (for deep lane density) within the same rack structure. Lower levels may feature standard selective beams for case-picking, while upper levels use push-back, flow, or shuttle rails for dense pallet storage. This optimizes the entire vertical profile of your warehouse.",
    specs: [
      { label: "Configuration", value: "Mixed beam + rail levels" },
      { label: "Storage Logic", value: "Mixed (FIFO beams + LIFO/FIFO rails)" },
      { label: "Upright Heights", value: "16' to 45'" },
      { label: "Beam Levels", value: "Selective access for picking" },
      { label: "Rail Levels", value: "Push-back, flow, or shuttle" },
      { label: "Weight Capacity", value: "Varies by level type" },
      { label: "Flexibility", value: "Each level independently configurable" },
      { label: "Cost Tier", value: "Medium-High" },
    ],
    features: [
      "Mix selective, push-back, flow, and shuttle in one structure",
      "Lower levels for hand-picking, upper for pallet storage",
      "Each level independently configured",
      "Optimizes vertical warehouse profile",
      "Reduces need for separate pick and bulk zones",
      "Compatible with various material handling equipment",
    ],
    pros: [
      "Best vertical space utilization",
      "Combines picking and bulk storage in one structure",
      "Reduces total warehouse footprint needed",
      "Flexible – each level serves a different function",
      "Optimizes labor by reducing travel between zones",
      "Can be adapted as operations evolve",
    ],
    cons: [
      "Complex design and engineering required",
      "Higher upfront design costs",
      "Multiple material handling systems may be needed",
      "More training required for operators",
      "Not ideal for simple, uniform storage needs",
    ],
    useCases: [
      { industry: "E-Commerce Fulfillment", icon: ShoppingCart, description: "Pick levels below, replenishment stock above" },
      { industry: "Retail Distribution", icon: Package, description: "Case-pick lower levels, bulk pallet upper" },
      { industry: "Food & Beverage", icon: Warehouse, description: "Mixed case-pick and full-pallet operations" },
      { industry: "Automotive Parts", icon: Factory, description: "Small-parts picking below, bulk components above" },
      { industry: "Wholesale Distribution", icon: Truck, description: "Multi-format order fulfillment" },
    ],
    compliance: { rmi: true, ansi: true, osha: true, seismic: true },
    faqs: [
      { question: "What is a hybrid beam + rail system?", answer: "It combines different storage types within one rack structure. For example, the bottom 2 levels might use standard beams for case-picking, while the top 3 levels use push-back rails for dense pallet storage. Each level is configured for its specific purpose." },
      { question: "When should I consider a hybrid system?", answer: "Consider hybrid when you need both case-picking and pallet storage, your warehouse has significant vertical space, or you want to combine pick and bulk zones to reduce forklift travel and improve efficiency." },
      { question: "Is hybrid racking more expensive?", answer: "The racking cost is higher due to mixed components, and the design engineering is more complex. However, the operational savings from combining zones, reducing footprint, and improving labor efficiency often deliver strong ROI." },
      { question: "What combinations are most common?", answer: "The most popular hybrid is selective beams on lower levels with push-back rails on upper levels. Other combinations include selective + flow, selective + shuttle, and even carton flow on lower levels with pallet storage above." },
      { question: "Can hybrid systems be reconfigured later?", answer: "Yes, one advantage of hybrid systems is that beam levels can be converted to rail levels (and vice versa) as your operation changes. This flexibility makes hybrid a good long-term investment for evolving businesses." },
    ],
    relatedProducts: ["selective", "push-back"],
    keyBenefit: "Multi-Function Vertical Optimization",
    densityRating: 4,
    accessibilityRating: 4,
     costEfficiency: "Best for mixed pick-and-store operations",
     mergedInto: "products",
   },
};

export const rackTypesList = Object.values(rackTypes);

/** Filtered list excluding entries merged into other pages */
export const activeRackTypesList = rackTypesList.filter((r) => !r.mergedInto);

export function getRackType(slug: string): RackType | undefined {
  return rackTypes[slug];
}

export function getRelatedRackTypes(currentSlug: string): RackType[] {
  const current = rackTypes[currentSlug];
  if (!current) return [];
  return current.relatedProducts
    .map((slug) => rackTypes[slug])
    .filter((r): r is RackType => !!r && !r.mergedInto);
}